import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, AlertCircle, X } from "lucide-react";

import { api } from "../../services/api";

/**
 * AssinaturaBanner — Banner soft block exibido no Kanban e demais páginas do
 * empreendedor quando a assinatura está pending/cancelled/expired/paused.
 *
 * Comportamento "soft block": NÃO bloqueia o módulo, apenas alerta o usuário
 * com CTA para resolver. Pode ser dispensado por sessão (X).
 *
 * Uso:
 *   <AssinaturaBanner negocioId={negocioId} />
 */
export default function AssinaturaBanner({ negocioId }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!negocioId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/delivery/negocios/${negocioId}/assinatura`);
        if (!cancelled) setData(r.data);
      } catch {
        /* silencioso */
      }
    })();
    return () => { cancelled = true; };
  }, [negocioId]);

  if (!data || dismissed) return null;

  // Não mostra banner se está ativa OU se é o estado "nenhuma" (trial)
  if (data.active || data.status === "nenhuma") return null;

  const cfg = _bannerConfig(data.status);

  return (
    <div
      className={`${cfg.bg} border-b ${cfg.border}`}
      data-testid="delivery-assinatura-banner"
      data-status={data.status}
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <AlertCircle className={`w-4 h-4 shrink-0 ${cfg.icon}`} />
        <div className="flex-1 min-w-0">
          <span className={`font-semibold ${cfg.text}`}>{cfg.title}</span>
          {data.motivo && (
            <span className={`ml-1 ${cfg.text} opacity-90`}>{data.motivo}</span>
          )}
        </div>
        <Link
          to={`/empreendedor/delivery/${negocioId}/assinatura`}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full bg-white/80 hover:bg-white font-semibold shrink-0"
          data-testid="delivery-assinatura-banner-cta"
        >
          <CreditCard className="w-3 h-3" /> Ver planos
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className={`p-1 rounded hover:bg-white/30 ${cfg.text}`}
          aria-label="Fechar"
          data-testid="delivery-assinatura-banner-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function _bannerConfig(status) {
  if (status === "pending") {
    return {
      bg: "bg-amber-50",
      border: "border-amber-200",
      icon: "text-amber-600",
      text: "text-amber-800",
      title: "Pagamento em processamento.",
    };
  }
  if (status === "expired") {
    return {
      bg: "bg-red-50",
      border: "border-red-200",
      icon: "text-red-600",
      text: "text-red-800",
      title: "Sua assinatura venceu.",
    };
  }
  if (status === "cancelled") {
    return {
      bg: "bg-red-50",
      border: "border-red-200",
      icon: "text-red-600",
      text: "text-red-800",
      title: "Assinatura cancelada.",
    };
  }
  if (status === "paused") {
    return {
      bg: "bg-amber-50",
      border: "border-amber-200",
      icon: "text-amber-600",
      text: "text-amber-800",
      title: "Assinatura pausada.",
    };
  }
  return {
    bg: "bg-amber-50",
    border: "border-amber-200",
    icon: "text-amber-600",
    text: "text-amber-800",
    title: "Atualize sua assinatura.",
  };
}
